import React from 'react';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Icon,
  TextField,
  useTheme
} from '@mui/material';
import { LegacyGrid as Grid } from '../../elements';
import type { Theme } from '@mui/material/styles';
import { colors } from '../../utils';
import { V4_DIALOG_CONTENT } from '../../theme/v4Compat';
import { extractApiErrorMessage, notifications } from '../../utils';
import { useTranslation } from 'react-i18next';
import {
  HealthSystemDomainChipNumbersCommandUpsertChipNumberCommand,
  HealthSystemDomainChipNumbersResultChipNumberViewModel,
  HealthSystemDomainFarmsResultFarmViewModel
} from '../../api/HealthSystemApi';
import { ApiService } from '../../hooks';
import { AnimalGenderSelect, FarmSelectModal } from '../../elements';
import { AnimalGender } from '../../models/animal';
import { formatChipNumber, parseChipNumber } from '../../utils/chipNumberMask';

/**
 * What the dialog edits. Editing passes a whole chip number range; creating passes `{}`, and the missing id is
 * what switches the dialog to create mode. Start and end stay unset until the user types them.
 */
export type ChipNumberDraft = Partial<
  Pick<HealthSystemDomainChipNumbersResultChipNumberViewModel, 'id' | 'start' | 'end' | 'farm' | 'gender'>
>;

/** The command as built from the draft: `start` and `end` stay unset until validateForm() has passed. */
type ChipNumberCommandDraft = Omit<HealthSystemDomainChipNumbersCommandUpsertChipNumberCommand, 'start' | 'end'> & {
  start?: number;
  end?: number;
};

interface CreateChipNumberModalProps {
  opened: boolean;
  employee: ChipNumberDraft;
  onModalAction: (result: boolean) => void;
}

const CreateUpdateChipNumberModal: React.FC<CreateChipNumberModalProps> = (props) => {
  const { opened, employee: initialChipNumber, onModalAction } = props;

  const styles = sxStyles(useTheme());
  const { t } = useTranslation();

  const apiService = new ApiService();

  const [isBusy, setIsBusy] = React.useState(false);
  const [rangeError, setRangeError] = React.useState(false);
  const [conflictingRanges, setConflictingRanges] = React.useState<
    HealthSystemDomainChipNumbersResultChipNumberViewModel[]
  >([]);
  const [isCheckingOverlap, setIsCheckingOverlap] = React.useState(false);
  const overlapTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  const [chipNumber, setChipNumber] = React.useState<ChipNumberDraft>(initialChipNumber);
  const [farm, setFarm] = React.useState<HealthSystemDomainFarmsResultFarmViewModel | null>(
    initialChipNumber?.farm || null
  );
  const [gender, setGender] = React.useState(AnimalGender.Unknown);

  const editMode = !!chipNumber.id;
  const dialogTitle = editMode ? t('f-chip-numbers-edit-title') : t('f-chip-numbers-add-title');
  const saveButtonTitle = editMode ? t('f-com-edit') : t('f-com-create');

  React.useEffect(() => {
    if (opened) {
      setChipNumber({ ...initialChipNumber });
      setFarm(initialChipNumber?.farm || null);
      setGender(initialChipNumber?.gender || AnimalGender.Unknown);

      if (!editMode) {
        clearFields();
        setFarm(null);
      }
    }
  }, [opened]);

  const clearFields = () => {
    setChipNumber((prev) => ({ ...prev }));
    setFarm(null);
    setGender(AnimalGender.Unknown);
    setRangeError(false);
    setConflictingRanges([]);
    setIsCheckingOverlap(false);
  };

  const checkOverlap = (start: number | undefined, end: number | undefined) => {
    if (overlapTimer.current) clearTimeout(overlapTimer.current);

    if (!start || !end || start <= 0 || end <= 0 || start >= end) {
      setConflictingRanges([]);
      setIsCheckingOverlap(false);
      return;
    }

    setIsCheckingOverlap(true);

    overlapTimer.current = setTimeout(() => {
      apiService.api.chipNumbers
        .chipNumbersGetChipNumber({
          'Filter.RangeFrom': start,
          'Filter.RangeTo': end,
          Page: 1,
          Size: 200
        } as Parameters<typeof apiService.api.chipNumbers.chipNumbersGetChipNumber>[0])
        .then((result) => {
          const conflicts = result.data.data.filter((r) => start <= r.end && r.start <= end && r.id !== chipNumber.id);
          setConflictingRanges(conflicts);
        })
        .catch(() => {})
        .finally(() => setIsCheckingOverlap(false));
    }, 600);
  };

  const handleClose = () => onModalAction(false);

  const handleSave = () => {
    const command = buildCreateCommand();

    if (validateForm(command)) {
      setIsBusy(true);

      apiService.api.chipNumbers
        .chipNumbersCreateChipNumber(command)
        .then(() => {
          onModalAction(true);
        })
        .catch((e) => {
          notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
        })
        .finally(() => {
          setIsBusy(false);
        });
    }
  };

  const buildCreateCommand = (): ChipNumberCommandDraft => {
    return {
      id: chipNumber.id,
      start: chipNumber.start,
      end: chipNumber.end,
      gender: gender,
      farmId: farm?.id || null
    };
  };

  const validateForm = (
    input: ChipNumberCommandDraft
  ): input is HealthSystemDomainChipNumbersCommandUpsertChipNumberCommand => {
    let valid = true;

    if (!input.start || input.start === 0) {
      setRangeError(true);
      valid = false;
    }

    if (!input.end || input.end === 0) {
      setRangeError(true);
      valid = false;
    }

    if (input.start !== undefined && input.end !== undefined && input.start >= input.end) {
      setRangeError(true);
      valid = false;
    }

    return valid;
  };

  return (
    <Dialog
      id="createChipNumberDialog"
      open={opened}
      onClose={handleClose}
      sx={styles.dialog}
      maxWidth="lg"
      aria-labelledby="form-dialog-title"
    >
      <DialogTitle id="createChipNumbersTitle" sx={styles.dialogHeaderFooter}>
        {dialogTitle}
      </DialogTitle>

      <DialogContent sx={V4_DIALOG_CONTENT}>
        <Grid container direction="column" spacing={2} sx={styles.updateForm}>
          <Grid item container direction="column" spacing={1} sx={styles.formControl}>
            <Grid item>
              <TextField
                id="chipNumberStart"
                label={t('f-chip-numbers-start')}
                size="small"
                fullWidth
                variant="outlined"
                slotProps={{ htmlInput: { inputMode: 'numeric' } }}
                value={formatChipNumber(chipNumber.start)}
                onChange={(e) => {
                  const newValue = parseChipNumber(e.target.value);
                  if (newValue >= 0) {
                    setChipNumber({ ...chipNumber, start: newValue });
                    checkOverlap(newValue, chipNumber.end);
                  }
                }}
                error={rangeError}
                helperText={rangeError ? t('f-chip-numbers-validation-failed') : ''}
              />
            </Grid>

            <Grid item>
              <TextField
                id="chipNumberEnd"
                label={t('f-chip-numbers-end')}
                size="small"
                fullWidth
                variant="outlined"
                slotProps={{ htmlInput: { inputMode: 'numeric' } }}
                value={formatChipNumber(chipNumber.end)}
                onChange={(e) => {
                  const newValue = parseChipNumber(e.target.value);
                  if (newValue >= 0) {
                    setChipNumber({ ...chipNumber, end: newValue });
                    checkOverlap(chipNumber.start, newValue);
                  }
                }}
                error={rangeError}
                helperText={rangeError ? t('f-chip-numbers-validation-failed') : ''}
              />
            </Grid>

            {conflictingRanges.length > 0 && (
              <Grid item>
                <Alert severity="warning">
                  <strong>{t('f-chip-numbers-overlap-warning')}</strong>
                  <ul className="mt-1 mb-0 pl-4">
                    {conflictingRanges.map((r) => (
                      <li key={r.id}>
                        {formatChipNumber(r.start)} – {formatChipNumber(r.end)}
                        {r.farm?.name ? ` (${r.farm.name})` : ''}
                      </li>
                    ))}
                  </ul>
                </Alert>
              </Grid>
            )}

            <Grid item>
              <AnimalGenderSelect
                value={gender}
                allowAny={true}
                cssClass="!m-2 !min-w-[400px]"
                onAnimalGenderChange={(value) => {
                  setGender(value);
                }}
              />
            </Grid>

            <Grid item>
              <FarmSelectModal farm={farm} onChange={(farm) => setFarm(farm)} />
            </Grid>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={styles.dialogHeaderFooter}>
        {isBusy && <CircularProgress />}

        <Button onClick={handleClose} variant="outlined" startIcon={<Icon>{'close'}</Icon>} color="primary">
          {t('f-com-cancel')}
        </Button>

        <Button
          onClick={handleSave}
          id="addRuleBtn"
          disabled={isBusy || isCheckingOverlap}
          startIcon={<Icon>{'add_circle_outline'}</Icon>}
          variant="contained"
          color="primary"
        >
          {saveButtonTitle}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateUpdateChipNumberModal;

/** Was ./styles.ts (makeStyles); v9 `sx` values. */
const sxStyles = (theme: Theme) => {
  const fieldMaxWidth = 400;
  return {
    formControl: {
      margin: theme.spacing(1),
      minWidth: fieldMaxWidth
    },
    formControl2: {
      marginRight: theme.spacing(1),
      marginTop: theme.spacing(1),
      marginBottom: theme.spacing(1)
    },
    marginLeft: {
      marginLeft: theme.spacing(1)
    },
    ageRuleBtn: {
      maxWidth: 130,
      marginLeft: theme.spacing(1),
      marginBottom: theme.spacing(1)
    },
    dialogHeaderFooter: {
      background: colors.defaultGrey
    },
    dialog: {
      overflow: 'hidden'
    },
    updateForm: {
      minWidth: 250,
      justifyContent: 'center'
    },
    groupTitle: {}
  } as const;
};
